import { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { SharepointLink } from "@gb/contracts";
import { SharepointMark } from "@/components/gb/brand-marks";
import { Button } from "@/components/ui/button";
import { documentKeys, importKeys } from "@/features/project/documents-api";
import { useSyncSharepointNow } from "@/features/project/sharepoint-api";
import { formatMoment } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * How the project's SharePoint sync stands, at the top of Sources
 * (`plans/sharepoint-sync.md`, question 11): when the folder was last
 * checked, what is on its way, why it fails when it does, and the files it
 * does not take.
 *
 * At the top with the other lines of what is happening (`product/0015`): a
 * person who saved a file in SharePoint looks here for it first.
 */
export function SharepointLine({
  projectId,
  link,
  checking = false,
  onManage,
}: {
  projectId: string;
  link: SharepointLink;
  /** A poll is queued or running: its answer is on its way. */
  checking?: boolean;
  onManage: () => void;
}) {
  const syncNow = useSyncSharepointNow(projectId);
  const [showNotTaken, setShowNotTaken] = useState(false);
  useArrivals(projectId, link);

  const failing = link.health === "failing";
  const said =
    link.health === "waiting"
      ? "Reading the folder for the first time."
      : failing
        ? // No start while the organisation is not connected: no poll ran to fail.
          `${link.failingSince ? `Not reached since ${formatMoment(link.failingSince)}. ` : ""}${link.failure ?? ""}`.trim()
        : `Last checked ${formatMoment(link.lastCheckedAt!)}.${
            link.waiting > 0 ? ` ${link.waiting === 1 ? "1 file is" : `${link.waiting} files are`} on the way.` : ""
          }`;
  const notTaken = link.notTaken.length;

  return (
    <section
      aria-label="SharePoint"
      className={cn(
        "mb-4 rounded-card-lg border-[1.5px] px-5 py-3",
        failing ? "border-danger/40 bg-danger-bg" : "border-border bg-surface",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <SharepointMark className="size-4 shrink-0" />
        <p
          className={cn("min-w-0 flex-1 text-[0.86rem]", failing ? "font-semibold text-danger" : "text-ink-slate")}
          role="status"
          aria-live="polite"
        >
          <span className="font-semibold text-ink">{link.folderName}</span>
          {" · "}
          {said}
        </p>
        <Button variant="secondary" size="sm" disabled={syncNow.isPending || checking} onClick={() => syncNow.mutate()}>
          {(syncNow.isPending || checking) && <Loader2 size={14} className="animate-spin" />}
          {checking ? "Checking…" : failing ? "Try now" : "Sync now"}
        </Button>
        <Button variant="secondary" size="sm" onClick={onManage}>
          Manage
        </Button>
      </div>
      {notTaken > 0 && (
        <div className="mt-2">
          <button
            type="button"
            aria-expanded={showNotTaken}
            onClick={() => setShowNotTaken((open) => !open)}
            className="flex cursor-pointer items-center gap-1 text-[0.8rem] font-semibold text-muted hover:text-ink"
          >
            <ChevronDown size={14} aria-hidden className={cn("transition-transform", showNotTaken && "rotate-180")} />
            {notTaken === 1 ? "1 file in the folder is not taken" : `${notTaken} files in the folder are not taken`}
          </button>
          {showNotTaken && (
            <ul className="mt-1.5 flex flex-col gap-1 pl-5 text-[0.8rem] text-ink-slate">
              {link.notTaken.map((file) => (
                <li key={`${file.folder ?? ""}/${file.name}`}>
                  <span className="font-semibold text-ink">{file.name}</span>
                  {file.folder && <span className="text-muted"> · {file.folder}</span>}
                  <span className="text-muted"> · {file.reason}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {syncNow.error && (
        <p role="alert" className="mt-2 text-[0.85rem] font-semibold text-danger">
          {syncNow.error.message}
        </p>
      )}
    </section>
  );
}

/**
 * The shelves, the files and the imports are read again when the sync's
 * counts move: files arrive from SharePoint minutes after anybody here did
 * anything, so nothing else would say they had.
 */
function useArrivals(projectId: string, link: SharepointLink): void {
  const queryClient = useQueryClient();
  const seen = useRef<string | null>(null);
  // Not the last check's time, which moves every minute with nothing new.
  const key = `${link.files}/${link.waiting}`;
  useEffect(() => {
    if (seen.current === null) {
      seen.current = key;
      return;
    }
    if (seen.current === key) return;
    seen.current = key;
    void queryClient.invalidateQueries({ queryKey: ["project", projectId, "brain"] });
    void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
    void queryClient.invalidateQueries({ queryKey: importKeys.list(projectId) });
  }, [key, projectId, queryClient]);
}
